import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
} from 'react';
import { GlobalContext } from 'GlobalContext';
import api from 'api';
import { get } from 'utils/fetch';
import { message, Spin, Input, Select, Button } from 'antd';
import './AllInfoExpanded.css';
import { EditOutlined, CaretDownOutlined } from '@ant-design/icons';
import {
    AssetMainHeader,
    AssetMainBody,
    AssetGroupSummary,
} from './AllInfoExpandedComponents';
import { useFilter } from '../AssetManagerContext';
import { filterAssetEntry } from './filterUtil';
import { comprehensiveAssetFilter } from './filterUtil';
import { title2Field } from '../AssetManagerContext';

export function AllInfoExpanded() {
    const {
        manageAssetId,
        accessToken,
        assetManager,
        refreshSession,
        setManageAssetId,
    } = useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const { filteredText, visibleCols, assetFilters, filterLogic, groupBy } =
        useFilter();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [childMap, setChildMap] = useState({});
    // const loadMoreRef = useRef(null);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const limitCurFile = 50;
    const [sortBy, setSortBy] = useState(null); // 'address' or 'city'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [expandedMap, setExpandedMap] = useState({});
    const [prevRequest, setPrevRequest] = useState('');

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    const rowsLengthRef = useRef(0);

    useEffect(() => {
        rowsLengthRef.current = rows.length;
    }, [rows.length]);

    const observer = useRef();

    const prevGroupByRef = useRef(groupBy);
    const prevSortByRef = useRef(sortBy);

    useEffect(() => {
        const prevGroupBy = prevGroupByRef.current;
        const prevSortBy = prevSortByRef.current;

        const groupByChanged =
            JSON.stringify(prevGroupBy[0]) !== JSON.stringify(groupBy[0]);
        const sortByChanged = prevSortBy !== sortBy;

        // Case 1: first groupBy field changed → reset and fetch from start
        if (groupByChanged) {
            setRows([]);
            fetchExpandedInfo(
                limitCurFile,
                0,
                groupBy.length === 0 ? sortBy : title2Field[groupBy[0].field],
                groupBy.length === 0 ? sortOrder : groupBy[0].order
            );
        }
        // Case 2: sort changed and no groupBy → reset and fetch from start
        else if (sortByChanged && groupBy.length === 0) {
            setRows([]);
            fetchExpandedInfo(limitCurFile, 0, sortBy, sortOrder);
        }
        // Case 3: sort changed but groupBy exists → do nothing (keep local grouping)
        // (else normal infinite scroll)

        // Update refs for next comparison
        prevGroupByRef.current = groupBy;
        prevSortByRef.current = sortBy;
    }, [groupBy, sortBy, sortOrder]);

    const loadMoreRef = useCallback(
        (node) => {
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (
                    entries[0].isIntersecting &&
                    rowsLengthRef.current < totalCount
                ) {
                    fetchExpandedInfo(
                        limitCurFile,
                        rowsLengthRef.current,
                        groupBy.length === 0
                            ? sortBy
                            : title2Field[groupBy[0].field],
                        groupBy.length === 0 ? sortOrder : groupBy[0].order
                    );
                }
            });
            if (node) observer.current.observe(node);
        },
        [totalCount, sortBy, sortOrder, fetchExpandedInfo, groupBy]
    );

    async function fetchExpandedInfo(
        limit = limitCurFile,
        offset = 0,
        sortBy,
        sortOrder
    ) {
        const curRequest = `${limit}-${offset}-${sortBy}-${sortOrder}`;
        if (curRequest === prevRequest) return;
        setPrevRequest(curRequest);
        if (loadingMore) return;
        setLoadingMore(true);
        await get(
            api.asset.getAllAssetsExpanded(limit, offset, sortBy, sortOrder),
            (data) => {
                setRows((prevRows) => {
                    const allRows = [...prevRows, ...data];
                    setHasMore(allRows.length < totalCount);
                    return allRows;
                });
            },
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        setLoadingMore(false);
        refreshSession();
        setLoading(false);
    }

    async function fetchTotalCount() {
        await get(
            api.asset.allExpandedCount,
            setTotalCount,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
    }

    useEffect(() => {
        if (!accessToken) return;
        const init = async () => {
            await fetchTotalCount();
            // await fetchExpandedInfo();
        };
        init();
    }, [accessToken]);

    async function fetchChildren(parentId) {
        let response = await fetch(api.asset.getChildProperties(parentId), {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });
        const { data } = await response.json();
        refreshSession();
        return data;
    }

    const handleSort = (column) => {
        setExpandedMap({});
        var newSortOrder = 'asc';
        if (sortBy === column) {
            // toggle asc/desc
            newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            newSortOrder = 'asc';
        }
        setSortBy(column);
        setSortOrder(newSortOrder);

        if (rowsLengthRef.current >= totalCount) {
            // already loaded all, just sort locally
            const sortedRows = [...rows].sort((a, b) => {
                const aVal = a[column];
                const bVal = b[column];
                const isEmpty = (val) =>
                    val === null ||
                    val === undefined ||
                    val === '' ||
                    val === '{}' ||
                    (Array.isArray(val) && val.length === 0);
                // push null/undefined/empty string to the end
                const aEmpty = isEmpty(aVal);
                const bEmpty = isEmpty(bVal);

                if (aEmpty && bEmpty) return 0; // both empty, keep order
                if (aEmpty) return 1; // a empty → goes after b
                if (bEmpty) return -1; // b empty → goes after a

                // normal comparison
                if (aVal < bVal) return newSortOrder === 'asc' ? -1 : 1;
                if (aVal > bVal) return newSortOrder === 'asc' ? 1 : -1;
                return 0;
            });
            setRows(sortedRows);
            return;
        } else {
            if (groupBy.length === 0) {
                setRows([]);
            }
        }
    };

    function groupAssetsByFields(rows, groupFields = []) {
        if (groupFields.length === 0) return rows;

        const groupRecursively = (data, fields, level = 0) => {
            if (level >= fields.length) return data;

            const field = title2Field[fields[level]?.field];
            const groups = {};

            for (const row of data) {
                // key: actual value of cur field
                const key = row[field] ?? 'Unknown';
                if (!groups[key]) groups[key] = [];
                groups[key].push(row);
            }

            // Recursively group sublevels
            return Object.entries(groups).map(([key, groupRows]) => ({
                key,
                field,
                children: groupRecursively(groupRows, fields, level + 1),
            }));
        };

        return groupRecursively(rows, groupFields);
    }

    function HeaderEntry(props) {
        const { title, minWidth } = props;

        const freeze =
            title === 'Address'
                ? {
                      position: 'sticky',
                      left: 36, // adjust based on the width of the first column
                      background: 'white',
                  }
                : {};

        function show() {
            if (!(title in visibleCols)) {
                return true;
            }
            return visibleCols[title];
        }

        return (
            <>
                {show() && (
                    <th
                        style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            minWidth: minWidth || 'auto',
                            ...freeze,
                        }}
                        onClick={() => handleSort(title2Field[title])}
                    >
                        {title}
                        <span style={{ marginLeft: 5 }}>
                            {sortBy === title2Field[title]
                                ? sortOrder === 'asc'
                                    ? '↑'
                                    : '↓'
                                : '↕'}
                        </span>
                    </th>
                )}
            </>
        );
    }

    const handleToggle = async (rowId, parentId) => {
        const isExpanded = !!expandedMap[rowId];
        setExpandedMap((prev) => ({ ...prev, [rowId]: !isExpanded }));

        if (!isExpanded && !childMap[parentId]) {
            const children = await fetchChildren(parentId);
            setChildMap((prev) => ({ ...prev, [parentId]: children }));
        }
    };

    const renderRowsGroup = (data, curGroup = []) => {
        if (!Array.isArray(data)) return null;
        if (data.length === 0) return null;
        if ('key' in data[0]) {
            return data
                .sort((a, b) => {
                    const aVal = a.key;
                    const bVal = b.key;
                    const isEmpty = (val) =>
                        val === null ||
                        val === undefined ||
                        val === '' ||
                        val === '{}' ||
                        (Array.isArray(val) && val.length === 0);
                    // push null/undefined/empty string to the end
                    const aEmpty = isEmpty(aVal);
                    const bEmpty = isEmpty(bVal);

                    if (aEmpty && bEmpty) return 0; // both empty, keep order
                    if (aEmpty) return 1; // a empty → goes after b
                    if (bEmpty) return -1; // b empty → goes after a

                    // normal comparison
                    if (aVal < bVal)
                        return groupBy[curGroup.length].order === 'asc'
                            ? -1
                            : 1;
                    if (aVal > bVal)
                        return groupBy[curGroup.length].order === 'asc'
                            ? 1
                            : -1;
                    return 0;
                })
                .map((group, idx) => {
                    const nextGroup = [
                        ...curGroup,
                        {
                            key: group.key,
                            field: groupBy[curGroup.length].field,
                        },
                    ];
                    const uniqueKey = `${curGroup
                        .map((g) => g.key)
                        .join('-')}-${group.key}-${idx}`;
                    return (
                        <React.Fragment key={uniqueKey}>
                            {renderRowsGroup(group.children, nextGroup)}
                        </React.Fragment>
                    );
                });
        }

        const groupTitle = curGroup
            .map((groupItem) => {
                return `${groupItem.field}: ${groupItem.key}`;
            })
            .join(', ');
        const groupKey = `group-${curGroup.map((g) => g.key).join('-')}`;
        return (
            <>
                <tr key={`${groupKey}-header`} style={{ display: '' }}>
                    <td
                        colSpan={2}
                        style={{
                            fontWeight: 'bold',
                            background: '#f0f0f0',
                            padding: '8px',
                            textAlign: 'left',
                            position: 'sticky',
                            left: 0,
                            border: 0,
                            borderRadius: '10px 10px 0 0',
                        }}
                    >
                        {groupTitle}
                    </td>
                    <td colSpan={700} style={{ border: 0 }}></td>
                </tr>
                {renderRows(data, null, 0, groupTitle)}
            </>
        );
    };
    // Recursive row renderer
    const renderRows = (data, parentId, level = 0, groupTitle = '') => {
        const filteredData = data.filter((row) => {
            if (level !== 0) {
                return true;
            }
            return comprehensiveAssetFilter(
                row,
                assetFilters.filter((filter) => {
                    if (filter.value === null) {
                        return false;
                    }
                    if (
                        filter.field in visibleCols &&
                        !visibleCols[filter.field]
                    ) {
                        return false;
                    }
                    return true;
                }),
                filterLogic
            );
        });
        return (
            <>
                {filteredData
                    // sort within group
                    .sort((a, b) => {
                        if (level !== 0) {
                            return 0;
                        }
                        if (groupTitle === '') {
                            return 0;
                        }
                        const aVal = a[sortBy];
                        const bVal = b[sortBy];
                        const isEmpty = (val) =>
                            val === null ||
                            val === undefined ||
                            val === '' ||
                            val === '{}' ||
                            (Array.isArray(val) && val.length === 0);
                        // push null/undefined/empty string to the end
                        const aEmpty = isEmpty(aVal);
                        const bEmpty = isEmpty(bVal);

                        if (aEmpty && bEmpty) return 0; // both empty, keep order
                        if (aEmpty) return 1; // a empty → goes after b
                        if (bEmpty) return -1; // b empty → goes after a

                        // normal comparison
                        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
                    })
                    // sort child properties
                    .sort((a, b) => {
                        if (level === 0) {
                            return 0;
                        }
                        if (a.address < b.address) {
                            return -1;
                        }
                        return 1;
                    })
                    .flatMap((row, idx) => [
                        <AssetMainBody
                            key={`${level}-${idx}-${groupTitle}`}
                            row={row}
                            idx={idx}
                            setManageAssetId={setManageAssetId}
                            level={level}
                            expandedMap={expandedMap}
                            setExpandedMap={setExpandedMap}
                            childMap={childMap}
                            setChildMap={setChildMap}
                            fetchChildren={fetchChildren}
                            handleToggle={handleToggle}
                        />,
                        ...(expandedMap[`${idx}-${row.property_id}`] &&
                        childMap[row.property_id]
                            ? renderRows(
                                  childMap[row.property_id],
                                  row.property_id,
                                  level + 1
                              )
                            : []),
                    ])}
                {groupTitle !== '' && <AssetGroupSummary data={filteredData} />}
            </>
        );
    };
    return (
        <div style={{ padding: 0 }}>
            {/* <h2>Infinite Scroll + Parent-Child Table</h2> */}
            <table
                border={0}
                cellPadding={5}
                style={{ width: '100%' }}
                id="asset_table"
            >
                <thead
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white', // so text doesn't overlap
                        zIndex: 1,
                    }}
                >
                    <tr>
                        <th
                            style={{
                                width: '36px',
                                position: 'sticky',
                                left: 0, // adjust based on the width of the first column
                                background: 'white',
                            }}
                        ></th>
                        {rows.length > 0 && (
                            <AssetMainHeader HeaderEntry={HeaderEntry} />
                        )}
                    </tr>
                </thead>
                <tbody>
                    {/*{renderRows(groupAssetsByFields(rows, groupBy))}*/}
                    {/* {renderRows(rows, groupBy)} */}
                    {groupBy.length === 0 &&
                        renderRows(
                            rows.filter((row) => {
                                return comprehensiveAssetFilter(
                                    row,
                                    assetFilters.filter((filter) => {
                                        if (filter.value === null) {
                                            return false;
                                        }
                                        if (
                                            filter.field in visibleCols &&
                                            !visibleCols[filter.field]
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }),
                                    filterLogic
                                );
                            }),
                            groupBy
                        )}
                    {groupBy.length > 0 &&
                        renderRowsGroup(
                            groupAssetsByFields(
                                rows.filter((row) => {
                                    return comprehensiveAssetFilter(
                                        row,
                                        assetFilters.filter((filter) => {
                                            if (filter.value === null) {
                                                return false;
                                            }
                                            if (
                                                filter.field in visibleCols &&
                                                !visibleCols[filter.field]
                                            ) {
                                                return false;
                                            }
                                            return true;
                                        }),
                                        filterLogic
                                    );
                                }),
                                groupBy
                            )
                        )}
                    <tr ref={loadMoreRef}>
                        <td
                            colSpan={2}
                            style={{
                                textAlign: 'center',
                                border: 0,
                                position: 'sticky',
                                left: 0,
                            }}
                        >
                            {hasMore ? 'Loading more...' : 'No more rows'}
                        </td>
                        <td
                            colSpan={700}
                            style={{ textAlign: 'center', border: 0 }}
                        ></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
